import React, { useState, useMemo } from 'react';
import { getCountries, getCountryCallingCode, parsePhoneNumberFromString, type CountryCode } from 'libphonenumber-js/mobile';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { HitlInterrupt } from '@/store/chatStore';
import { ShieldCheck, Check, Loader2, GraduationCap, ExternalLink, Phone, User, Mail, Smartphone } from 'lucide-react';

// Build sorted country list once (outside component to avoid re-computation)
const displayNames = new Intl.DisplayNames(['es'], { type: 'region' });
const COUNTRY_OPTIONS = getCountries()
    .map((code) => ({
        code,
        callingCode: `+${getCountryCallingCode(code)}`,
        name: displayNames.of(code) ?? code,
    }))
    .sort((a, b) => a.name.localeCompare(b.name, 'es'));

interface HitlApprovalMessageProps {
    interrupt: HitlInterrupt;
    onDecision: (decision: 'approve' | 'reject' | 'edit', messageOrAction?: string | { name: string; args: Record<string, any> }) => void;
}

/**
 * Obtiene la información descriptiva de la acción según el tipo de herramienta
 */
function getActionInfo(toolName: string): {
    title: string;
} {
    const actionInfo: Record<string, { title: string }> = {
        'inscribir_usuario': {
            title: 'Confirmar inscripción',
        },
        'mark_human_requested': {
            title: 'Datos de contacto',
        },
        'search_learning_data': {
            title: 'Búsqueda de cursos',
        },
        'search_business_knowledge': {
            title: 'Consulta de información',
        },
    };

    const info = actionInfo[toolName] || {
        title: toolName,
    };

    return {
        title: info.title,
    };
}

// ─── Mobile format hints per country ─────────────────────────────────────────

interface CountryMobileInfo {
    /** Short guidance shown below the input at all times. */
    hint: string;
    /** Placeholder text for the national-number input. */
    placeholder: string;
}

const COUNTRY_MOBILE_INFO: Partial<Record<CountryCode, CountryMobileInfo>> = {
    AR: { hint: 'Incluí el 9 antes del código de área. Ej: 9 11 1234 5678 → 91112345678', placeholder: 'Ej: 91112345678' },
    MX: { hint: 'Ingresá los 10 dígitos sin prefijo. Ej: 55 1234 5678 → 5512345678',       placeholder: 'Ej: 5512345678' },
    CL: { hint: 'Empezá con 9 seguido de 8 dígitos. Ej: 9 5678 9012 → 956789012',           placeholder: 'Ej: 956789012' },
    CO: { hint: 'Empezá con 3, seguido de 9 dígitos. Ej: 300 123 4567 → 3001234567',        placeholder: 'Ej: 3001234567' },
    BR: { hint: 'Ingresá DDD + 9 + número (11 dígitos). Ej: 11 9 1234 5678 → 11912345678',  placeholder: 'Ej: 11912345678' },
    UY: { hint: 'Empezá con 09 (9 dígitos). Ej: 091 234 567 → 091234567',                   placeholder: 'Ej: 091234567' },
    PY: { hint: 'Empezá con 09 (10 dígitos). Ej: 0981 123 456 → 0981123456',               placeholder: 'Ej: 0981123456' },
    BO: { hint: 'Empezá con 6 o 7 (8 dígitos). Ej: 70123456',                              placeholder: 'Ej: 70123456' },
    PE: { hint: 'Empezá con 9 (9 dígitos). Ej: 912 345 678 → 912345678',                    placeholder: 'Ej: 912345678' },
    EC: { hint: 'Empezá con 09 (10 dígitos). Ej: 099 123 4567 → 0991234567',               placeholder: 'Ej: 0991234567' },
    VE: { hint: 'Empezá con 04XX (11 dígitos). Ej: 0412 123 4567 → 04121234567',           placeholder: 'Ej: 04121234567' },
    ES: { hint: 'Empezá con 6 o 7 (9 dígitos). Ej: 612 345 678 → 612345678',               placeholder: 'Ej: 612345678' },
    US: { hint: 'Ingresá los 10 dígitos sin prefijo. Ej: 212 555 1234 → 2125551234',        placeholder: 'Ej: 2125551234' },
    CA: { hint: 'Ingresá los 10 dígitos sin prefijo. Ej: 416 555 1234 → 4165551234',        placeholder: 'Ej: 4165551234' },
};

const DEFAULT_MOBILE_HINT = (callingCode: string) =>
    `Ingresá solo el número de celular, sin el código de país (+${callingCode}).`;

// ─── Contact form for mark_human_requested ───────────────────────────────────

interface ContactFormState {
    nombre_completo: string;
    email: string;
    pais: CountryCode;
    telefono: string; // national number (area code implicit)
}

interface HitlContactFormProps {
    interrupt: HitlInterrupt;
    onDecision: HitlApprovalMessageProps['onDecision'];
}

function parseInitialPhone(raw?: string | null): { pais: CountryCode; telefono: string } {
    if (!raw) return { pais: 'AR' as CountryCode, telefono: '' };
    try {
        const parsed = parsePhoneNumberFromString(raw);
        if (parsed?.country && parsed.nationalNumber) {
            return { pais: parsed.country as CountryCode, telefono: parsed.nationalNumber };
        }
    } catch { /* ignore unparseable values */ }
    return { pais: 'AR' as CountryCode, telefono: '' };
}

function HitlContactForm({ interrupt, onDecision }: HitlContactFormProps) {
    const [isProcessing, setIsProcessing] = useState(false);
    const [form, setForm] = useState<ContactFormState>(() => {
        const { pais, telefono } = parseInitialPhone(interrupt.toolArgs.telefono);
        return {
            nombre_completo: interrupt.toolArgs.nombre_completo ?? '',
            email: interrupt.toolArgs.email ?? '',
            pais,
            telefono,
        };
    });

    const selectedCountry = COUNTRY_OPTIONS.find(c => c.code === form.pais);
    const countryMobileInfo = COUNTRY_MOBILE_INFO[form.pais];

    const phoneValidation = useMemo(() => {
        const raw = form.telefono.trim();
        if (!raw) return { valid: false, error: null };
        try {
            const parsed = parsePhoneNumberFromString(raw, form.pais);
            if (!parsed || !parsed.isValid()) {
                return { valid: false, error: `Número inválido para ${selectedCountry?.name ?? form.pais}` };
            }
            const type = parsed.getType();
            const isMobile = type === 'MOBILE' || type === 'FIXED_LINE_OR_MOBILE';
            if (!isMobile) {
                return { valid: false, error: 'Este número no corresponde a un celular / móvil' };
            }
            return { valid: true, error: null };
        } catch {
            return { valid: false, error: 'Número inválido' };
        }
    }, [form.telefono, form.pais, selectedCountry]);

    const isComplete =
        form.nombre_completo.trim().length > 0 &&
        form.email.trim().length > 0 &&
        phoneValidation.valid;

    const handleConfirm = () => {
        if (!isComplete) return;
        setIsProcessing(true);
        // Re-parse to obtain canonical E.164 regardless of what the user typed
        // (e.g. "+5493815..." and "93815..." both yield "+5493815..." correctly)
        const parsed = parsePhoneNumberFromString(form.telefono.trim(), form.pais)!;
        onDecision('edit', {
            name: 'mark_human_requested',
            args: {
                reason: interrupt.toolArgs.reason ?? 'El usuario solicitó asistencia humana.',
                nombre_completo: form.nombre_completo.trim(),
                email: form.email.trim(),
                telefono: parsed.number,
            },
        });
    };

    const handleCancel = () => {
        setIsProcessing(true);
        onDecision('reject', 'El usuario canceló la solicitud de asistencia humana.');
    };

    const description = interrupt.description || 'Para conectarte con un operador necesitamos tus datos de contacto.';

    return (
        <div className="flex w-full justify-start animate-in fade-in-50 slide-in-from-bottom-2 duration-300 my-2">
            <Card className="w-full max-w-[95%] shadow-sm border bg-card overflow-hidden">
                <div className="p-4 space-y-4">
                    {/* Header */}
                    <div className="flex items-start gap-3">
                        <div className="shrink-0 mt-0.5 p-1.5 bg-primary/10 rounded-full">
                            <Phone className="w-4 h-4 text-primary" />
                        </div>
                        <div className="space-y-1">
                            <h4 className="text-sm font-medium text-foreground leading-none">
                                Datos de contacto
                            </h4>
                            <p className="text-sm text-muted-foreground leading-relaxed">
                                {description}
                            </p>
                        </div>
                    </div>

                    {/* Contact form */}
                    <div className="space-y-3">
                        {/* Nombre completo */}
                        <div className="space-y-1">
                            <Label htmlFor={`nombre-${interrupt.id}`} className="text-xs font-medium flex items-center gap-1.5">
                                <User className="w-3 h-3" />
                                Nombre completo <span className="text-destructive">*</span>
                            </Label>
                            <Input
                                id={`nombre-${interrupt.id}`}
                                value={form.nombre_completo}
                                onChange={(e) => setForm(p => ({ ...p, nombre_completo: e.target.value }))}
                                placeholder="Ej: Juan Pérez"
                                disabled={isProcessing}
                                className="h-8 text-sm"
                            />
                        </div>

                        {/* Email */}
                        <div className="space-y-1">
                            <Label htmlFor={`email-${interrupt.id}`} className="text-xs font-medium flex items-center gap-1.5">
                                <Mail className="w-3 h-3" />
                                Email <span className="text-destructive">*</span>
                            </Label>
                            <Input
                                id={`email-${interrupt.id}`}
                                type="email"
                                value={form.email}
                                onChange={(e) => setForm(p => ({ ...p, email: e.target.value }))}
                                placeholder="Ej: juan@ejemplo.com"
                                disabled={isProcessing}
                                className="h-8 text-sm"
                            />
                        </div>

                        {/* Phone: country selector + national number */}
                        <div className="space-y-1">
                            <Label className="text-xs font-medium flex items-center gap-1.5">
                                <Smartphone className="w-3 h-3" />
                                Celular / Móvil <span className="text-destructive">*</span>
                            </Label>
                            <div className="flex gap-2">
                                {/* Country selector */}
                                <select
                                    value={form.pais}
                                    onChange={(e) => setForm(p => ({ ...p, pais: e.target.value as CountryCode, telefono: '' }))}
                                    disabled={isProcessing}
                                    aria-label="País"
                                    className="h-8 rounded-md border border-input bg-background px-2 text-sm focus:outline-none focus:ring-1 focus:ring-ring disabled:opacity-50 shrink-0 w-[130px]"
                                >
                                    {COUNTRY_OPTIONS.map(({ code, callingCode, name }) => (
                                        <option key={code} value={code}>
                                            {name} ({callingCode})
                                        </option>
                                    ))}
                                </select>

                                {/* National number */}
                                <Input
                                    id={`telefono-${interrupt.id}`}
                                    type="tel"
                                    value={form.telefono}
                                    onChange={(e) => {
                                        const raw = e.target.value;
                                        // If user pastes a full E.164 (e.g. +5491112345678),
                                        // extract only the national number for the selected country.
                                        if (raw.startsWith('+')) {
                                            const parsed = parsePhoneNumberFromString(raw);
                                            if (parsed?.nationalNumber) {
                                                setForm(p => ({ ...p, telefono: parsed.nationalNumber }));
                                                return;
                                            }
                                        }
                                        setForm(p => ({ ...p, telefono: raw }));
                                    }}
                                    placeholder={countryMobileInfo?.placeholder ?? `Ej: número sin +${getCountryCallingCode(form.pais)}`}
                                    disabled={isProcessing}
                                    className="h-8 text-sm flex-1"
                                    aria-label="Número de celular"
                                />
                            </div>
                            {/* Always-visible format hint for the selected country */}
                            <p className="text-xs text-muted-foreground">
                                {countryMobileInfo?.hint ?? DEFAULT_MOBILE_HINT(getCountryCallingCode(form.pais))}
                            </p>
                            {/* Inline error shown only after the user has typed something */}
                            {phoneValidation.error && (
                                <p className="text-xs text-destructive">{phoneValidation.error}</p>
                            )}
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center justify-end gap-2 pt-2 border-t border-border/40">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleCancel}
                            disabled={isProcessing}
                            className="h-8 px-3 text-xs font-medium text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                        >
                            {isProcessing ? (
                                <Loader2 className="mr-2 w-3 h-3 animate-spin" />
                            ) : null}
                            Cancelar
                        </Button>
                        <Button
                            size="sm"
                            onClick={handleConfirm}
                            disabled={isProcessing || !isComplete}
                            className="h-8 px-4 text-xs font-medium bg-primary text-primary-foreground shadow-sm hover:bg-primary/90"
                        >
                            {isProcessing ? (
                                <Loader2 className="mr-2 w-3 h-3 animate-spin" />
                            ) : (
                                <Check className="mr-2 w-3 h-3" />
                            )}
                            Confirmar
                        </Button>
                    </div>
                </div>
            </Card>
        </div>
    );
}

// ─── Generic approval for other tools ────────────────────────────────────────

/**
 * Componente de mensaje de aprobación HITL que se muestra como parte del chat
 */
export function HitlApprovalMessage({ interrupt, onDecision }: HitlApprovalMessageProps) {
    // For mark_human_requested use the editable contact form
    if (interrupt.toolName === 'mark_human_requested') {
        return <HitlContactForm interrupt={interrupt} onDecision={onDecision} />;
    }

    const [isProcessing, setIsProcessing] = useState(false);
    const [selectedDecision, setSelectedDecision] = useState<'approve' | 'reject' | null>(null);
    const [acceptedTerms, setAcceptedTerms] = useState(true);

    const handleApprove = () => {
        if (!acceptedTerms) return;
        setIsProcessing(true);
        setSelectedDecision('approve');
        onDecision('approve');
    };

    const handleReject = () => {
        setIsProcessing(true);
        setSelectedDecision('reject');
        onDecision('reject', 'El usuario rechazó la acción');
    };

    const actionInfo = getActionInfo(interrupt.toolName);

    // Usar la descripción del backend si está disponible, sino usar fallback
    const description = interrupt.description || 'Necesito tu autorización para continuar con esta acción.';

    return (
        <div className="flex w-full justify-start animate-in fade-in-50 slide-in-from-bottom-2 duration-300 my-2">
            <Card className="w-full max-w-[95%] shadow-sm border bg-card overflow-hidden">
                <div className="p-4 space-y-4">
                    {/* Header & Description */}
                    <div className="flex items-start gap-3">
                        <div className="shrink-0 mt-0.5 p-1.5 bg-primary/10 rounded-full">
                            <ShieldCheck className="w-4 h-4 text-primary" />
                        </div>
                        <div className="space-y-1">
                            <h4 className="text-sm font-medium text-foreground leading-none">
                                {actionInfo.title}
                            </h4>
                            <p className="text-sm text-muted-foreground leading-relaxed">
                                {description}
                            </p>
                        </div>
                    </div>

                    {/* Course Info - Compact Horizontal Card */}
                    {interrupt.courseInfo && (
                        <div className="flex items-center gap-3 p-3 rounded-md bg-muted/40 border border-border/50">
                            <div className="shrink-0 p-2 bg-background rounded-md border shadow-sm">
                                <GraduationCap className="w-4 h-4 text-primary" />
                            </div>
                            <div className="flex-1 min-w-0 space-y-1">
                                <p className="text-sm font-medium text-foreground truncate">
                                    {interrupt.courseInfo.courseName}
                                </p>
                                {interrupt.courseInfo.description && (
                                    <p className="text-xs text-muted-foreground line-clamp-2">
                                        {interrupt.courseInfo.description}
                                    </p>
                                )}
                            </div>
                            {interrupt.courseInfo.url && interrupt.courseInfo.rootPath && (
                                <Button variant="link" size="sm" className="h-auto px-0 text-xs shrink-0 gap-1 text-primary" asChild>
                                    <a
                                        href={`${interrupt.courseInfo.rootPath}${interrupt.courseInfo.url}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        title="Ver detalles del curso"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <span>Ver</span>
                                        <ExternalLink className="w-3 h-3" />
                                    </a>
                                </Button>
                            )}
                        </div>
                    )}

                    {/* Footer: Terms & Actions */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-2 border-t border-border/40">
                        <div className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                id={`terms-${interrupt.id}`}
                                checked={acceptedTerms}
                                onChange={(e) => setAcceptedTerms(e.target.checked)}
                                className="w-3.5 h-3.5 rounded border-primary/30 text-primary focus:ring-primary cursor-pointer"
                            />
                            <label
                                htmlFor={`terms-${interrupt.id}`}
                                className="text-xs text-muted-foreground cursor-pointer select-none"
                            >
                                <a
                                    href="https://tyc.utn.centrodeelearning.com/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-primary hover:underline font-medium"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    Acepto condiciones de compra
                                </a>
                            </label>
                        </div>

                        <div className="flex items-center gap-2 self-end sm:self-auto">
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={handleReject}
                                disabled={isProcessing}
                                className="h-8 px-3 text-xs font-medium text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                            >
                                {isProcessing && selectedDecision === 'reject' && (
                                    <Loader2 className="mr-2 w-3 h-3 animate-spin" />
                                )}
                                Cancelar
                            </Button>
                            <Button
                                size="sm"
                                onClick={handleApprove}
                                disabled={isProcessing || !acceptedTerms}
                                className="h-8 px-4 text-xs font-medium bg-primary text-primary-foreground shadow-sm hover:bg-primary/90"
                            >
                                {isProcessing && selectedDecision === 'approve' ? (
                                    <Loader2 className="mr-2 w-3 h-3 animate-spin" />
                                ) : (
                                    <Check className="mr-2 w-3 h-3" />
                                )}
                                Confirmar
                            </Button>
                        </div>
                    </div>
                </div>
            </Card>
        </div>
    );
}

export default HitlApprovalMessage;
